import { withErrorBoundary } from 'app/components/error-boundary';
import { Box, Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { SearchInput } from 'app/design/primitives/input/search';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useAddConversationMembers, useOneChannel } from 'app/network/chat/channel';
import { useFindUsers, type FoundUser } from 'app/network/user';
import { MemberSearchResults } from 'app/screens/chat/components/member-search-results';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { ChatConversationMemberRole, CustomChannelType } from 'app/types/chat';
import type { NavigationScreen } from 'app/types/navigation';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable } from 'react-native';

const MemberRow = function MemberRow({
  user,
  selected,
  alreadyMember,
  onToggle,
}: {
  user: FoundUser;
  selected: boolean;
  alreadyMember: boolean;
  onToggle: (user: FoundUser) => void;
}) {
  const { t } = useTranslation('add-chat-members');
  const handlePress = () => {
    if (!alreadyMember) onToggle(user);
  };
  const firstName = user.profile?.firstName ?? '';
  const lastName = user.profile?.lastName ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    <Pressable disabled={alreadyMember} onPress={handlePress}>
      <Row
        alignItems="center"
        gap="spacing.general.x4"
        opacity={alreadyMember ? 0.5 : 1}
        paddingHorizontal="spacing.general.x8"
        paddingVertical="spacing.general.x2"
      >
        {user.profile?.picture ? (
          <PCImage
            aspectRatio={1}
            cachePolicy="memory-disk"
            height={44}
            recyclingKey={`${user.profile.picture}-add-row`}
            source={{ uri: getImageVariantUrl(user.profile.picture, 'mini') }}
            style={{ borderRadius: 22 }}
          />
        ) : (
          <Box backgroundColor="surface.disabled" borderRadius="radius.xl" height={44} width={44} />
        )}
        <Column flex={1} minWidth={0}>
          <Text fontWeight="600" numberOfLines={1}>
            {fullName}
          </Text>
          {alreadyMember ? (
            <Text color="text.secondary" fontSize={12}>
              {t('alreadyMember')}
            </Text>
          ) : null}
        </Column>
        {!alreadyMember ? (
          <Box
            alignItems="center"
            backgroundColor={selected ? 'surface.brand.default' : 'surface.disabled'}
            borderColor={selected ? 'surface.brand.default' : 'surface.secondary-neutral'}
            borderRadius="radius.sm"
            borderWidth={2}
            height={22}
            justifyContent="center"
            width={22}
          >
            {selected ? (
              <Text color="surface.default" fontSize={13} fontWeight="700">
                ✓
              </Text>
            ) : null}
          </Box>
        ) : null}
      </Row>
    </Pressable>
  );
};

const AddChatMembersScreenComponent: NavigationScreen<'add-chat-members'> = ({ route }) => {
  const { url } = route.params;
  const { t } = useTranslation('add-chat-members');
  const navigation = useNavigation<'add-chat-members'>({ headerTitle: t('title') });

  const [rawSearch, setRawSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<FoundUser[]>([]);

  const { data: channel, isPending: isChannelPending } = useOneChannel(url);
  const isAdmin =
    channel?.custom_type === CustomChannelType.Group && channel.current_user_role === ChatConversationMemberRole.Admin;
  const existingMemberIds = new Set((channel?.members ?? []).map((m) => m.user_id));

  const canSearch = rawSearch.trim().length >= 3;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: isSearching,
  } = useFindUsers(
    { search: rawSearch.trim(), groupIds: ['nearby'], sortBy: 'relevance', canReceiveInAppContact: true },
    { enabled: canSearch },
  );

  const results = canSearch ? (data?.pages.flatMap((p) => p.results) ?? []) : [];

  const selectedIds = new Set(selectedUsers.map((u) => u.id));
  const selectedUsersToAdd = selectedUsers.filter((user) => !existingMemberIds.has(user.id));

  const handleToggle = (user: FoundUser) => {
    setSelectedUsers((prev) =>
      prev.some((u) => u.id === user.id) ? prev.filter((u) => u.id !== user.id) : [...prev, user],
    );
  };

  const { mutate: addMembers, isPending: isAdding } = useAddConversationMembers({
    onSuccess: () => {
      navigation.goBack();
    },
  });

  const handleAdd = () => {
    if (selectedUsersToAdd.length === 0 || isAdding) return;
    addMembers({ url, userIds: selectedUsersToAdd.map((u) => u.id) });
  };

  const handleEndReached = () => {
    if (hasNextPage && !isSearching && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  const renderItem = ({ item }: { item: FoundUser }) => (
    <MemberRow
      user={item}
      selected={selectedIds.has(item.id)}
      alreadyMember={existingMemberIds.has(item.id)}
      onToggle={handleToggle}
    />
  );

  if (isChannelPending) {
    return (
      <Column
        alignItems="center"
        backgroundColor="surface.default"
        flex={1}
        justifyContent="center"
        padding="spacing.general.x8"
      >
        <ActivityIndicator />
      </Column>
    );
  }

  if (!isAdmin) {
    return (
      <Column
        alignItems="center"
        backgroundColor="surface.default"
        flex={1}
        justifyContent="center"
        padding="spacing.general.x8"
      >
        <Column gap="spacing.general.x4" maxWidth={420}>
          <Text textAlign="center" variant="bodyLarge">
            {t('forbiddenTitle')}
          </Text>
          <Text color="text.secondary" textAlign="center">
            {t('forbiddenBody')}
          </Text>
          <Button onPress={() => navigation.goBack()} variant="primary">
            {t('backButton')}
          </Button>
        </Column>
      </Column>
    );
  }

  return (
    <Column flex={1} backgroundColor="surface.default">
      <Column paddingHorizontal="spacing.general.x8" paddingTop="spacing.general.x4" gap="spacing.general.x2">
        <SearchInput onSubmit={setRawSearch} submitOnEmptyChange />
      </Column>

      <MemberSearchResults
        canSearch={canSearch}
        isSearching={isSearching || (results.length === 0 && isFetchingNextPage)}
        noResultsText={t('noResults')}
        onEndReached={handleEndReached}
        renderItem={renderItem}
        results={results}
      />

      <Column paddingHorizontal="spacing.general.x8" paddingVertical="spacing.general.x4">
        <Button
          disabled={selectedUsersToAdd.length === 0 || isAdding}
          loading={isAdding}
          onPress={handleAdd}
          variant="primary"
        >
          {t('addButton', { count: selectedUsersToAdd.length })}
        </Button>
      </Column>
    </Column>
  );
};

export const AddChatMembersScreen: NavigationScreen<'add-chat-members'> = withOptionalMetrics(
  'AddChatMembersScreen',
  withErrorBoundary(AddChatMembersScreenComponent),
);
